import { Component, OnInit, ViewChild } from '@angular/core';
import { WorkflowDataService } from '../../services/workflow-data.service';
import { Table } from 'primeng/table';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-quotation-status',
  templateUrl: './quotation-status.component.html',
  styleUrls: ['./quotation-status.component.scss'],
  providers: [MessageService]
})
export class QuotationStatusComponent implements OnInit {
  isHeaderCollapsed = false;
  workflowItems: any[] = [];
  globalFilter: string = '';
  rowsPerPage: number = 10;
  rowsPerPageOptions: (number | { label: string; value: number })[] = [5, 10, 20, { label: 'All', value: 0 }];
  @ViewChild('table') table!: Table;
  selectedCells: { [row: number]: number } = {};

  constructor(
    private workflowDataService: WorkflowDataService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.workflowDataService.workflowItems$.subscribe({
      next: (items) => {
        this.workflowItems = items.filter(item => {
          const jobNum = item.data.jobNumber?.toString().trim();
          return jobNum && jobNum.toLowerCase().startsWith('kbx');
        });
        this.rowsPerPageOptions = [5, 10, 20, this.workflowItems.length];
        if (this.rowsPerPage === 0) {
          this.rowsPerPage = this.workflowItems.length;
        }
        this.initializeSelectedCells();
      },
      error: (err) => {
        console.error('Error fetching workflow items:', err);
        this.workflowItems = [];
        this.rowsPerPageOptions = [5, 10, 20, this.workflowItems.length];
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load workflow items'
        });
      }
    });
  }

  initializeSelectedCells(): void {
    this.selectedCells = {};
    this.workflowItems.forEach((item, index) => {
      const status = item.data?.status;
      if (status) {
        const colIndex = this.getColumnIndexForStatus(status);
        if (colIndex) {
          this.selectedCells[index] = colIndex;
        }
      }
    });
  }

  private getColumnIndexForStatus(status: string): number {
    const statusMap: { [key: string]: number } = {
      'Production Order Release': 2,
      'Planning': 3,
      'Shearing & Punching': 4,
      'Bending': 5,
      'Welding': 6,
      'Powder Coating': 7,
      'Assembling/Rubber Beading': 8,
      'Packing': 9,
      'Complete': 10
    };
    return statusMap[status] || 0;
  }

  private getStatusName(col: number): string {
    const statusMap: { [key: number]: string } = {
      2: 'Production Order Release',
      3: 'Planning',
      4: 'Shearing & Punching',
      5: 'Bending',
      6: 'Welding',
      7: 'Powder Coating',
      8: 'Assembling/Rubber Beading',
      9: 'Packing',
      10: 'Complete'
    };
    return statusMap[col] || '';
  }

  onHeaderCollapse(isCollapsed: boolean): void {
    this.isHeaderCollapsed = isCollapsed;
    console.log('Header collapsed:', isCollapsed);
  }

  onGlobalFilter(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (this.table && input) {
      this.table.filterGlobal(input.value, 'contains');
    }
  }

  onColumnFilter(event: Event, field: string): void {
    const input = event.target as HTMLInputElement;
    if (this.table && input) {
      this.table.filter(input.value, field, 'contains');
    }
  }

  onPageChange(event: any): void {
    this.rowsPerPage = event.rows === 0 ? this.workflowItems.length : event.rows;
    console.log('Rows per page changed:', this.rowsPerPage);
  }

  onCellClick(row: number, col: number): void {
    if (col <= 1) return;
    this.selectedCells[row] = col;
    this.table._value = [...this.workflowItems]; // Trigger table refresh
  }

  getCellClass(row: number, col: number): string {
    const selectedCol = this.selectedCells[row];
    if (selectedCol === 10 && col === 10) {
      return 'cell-green';
    }
    if (selectedCol === col && col > 1) {
      return 'cell-olivegreen';
    }
    if (selectedCol && col < selectedCol && col > 1) {
      return 'cell-green';
    }
    return '';
  }

  getCellContent(row: number, col: number): string {
    const selectedCol = this.selectedCells[row];
    if (selectedCol === col && col > 1) {
      return '✓';
    }
    if (selectedCol && col < selectedCol && col > 1) {
      return '✓';
    }
    return '-';
  }

  saveStatuses(): void {
    Object.keys(this.selectedCells).forEach(rowIndex => {
      const row = parseInt(rowIndex, 10);
      const col = this.selectedCells[row];
      if (col && col > 1) {
        const quotationNo = this.workflowItems[row]?.data?.quotationNo;
        const status = this.getStatusName(col);
        const statusId = this.workflowItems[row]?.data?.statusId;
        if (quotationNo && status) {
          this.workflowDataService.saveQuotationStatus(quotationNo, status, statusId).subscribe({
            next: (response) => {
              console.log(`Status saved for ${quotationNo}: ${status}`, response);
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `Status saved for ${quotationNo}: ${status}`
              });
              // Update local workflow item with new status and statusId
              this.workflowItems[row].data.status = status;
              this.workflowItems[row].data.statusId = response.data.id; // Update statusId from response
              this.workflowDataService.updateWorkflowItems([...this.workflowItems]);
            },
            error: (err) => {
              console.error(`Failed to save status for ${quotationNo}:`, err);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: `Failed to save status for ${quotationNo}: ${err.message || 'Unknown error'}`
              });
            }
          });
        }
      }
    });
  }
}